import type { ProfileResource } from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import { PRACTITIONER_ENTITY_ID_SYSTEM } from "@repo/api";
import { practitionerIdSchema } from "@repo/api/schemas";
import { usePractitioner } from "@repo/data-store";
import type { PractitionerId } from "@repo/domain";

export function practitionerDomainIdFromProfile(profile: ProfileResource | undefined): PractitionerId | undefined {
  if (profile?.resourceType !== "Practitioner") return undefined;
  const value = profile.identifier?.find((identifier) => identifier.system === PRACTITIONER_ENTITY_ID_SYSTEM)?.value;
  return value ? practitionerIdSchema.parse(value) : undefined;
}

/**
 * The logged-in profile's FHIR row id — for read paths that match on a literal
 * `Practitioner/<row id>` reference and have no domain identity to match on:
 * `useNotifications` (Communication.recipient), `usePatients({ careManagerId })`
 * (EpisodeOfCare.careManager) and the ai-engine chat context. Passing
 * `practitionerDomainIdFromProfile` to any of them matches nothing and returns
 * an empty list, silently. Prefer that one everywhere else.
 */
export function practitionerFhirIdFromProfile(profile: ProfileResource | undefined): string | undefined {
  return profile?.resourceType === "Practitioner" ? profile.id : undefined;
}

/** Resolve the logged-in FHIR profile to its stable practitioner identity. */
export function useCurrentPractitioner() {
  const practitionerId = practitionerDomainIdFromProfile(useMedplum().getProfile());
  return usePractitioner(practitionerId);
}
